import { ReactComponent as Field } from "./assets/baseball-court.svg";
import * as service from "../../service";
import React, {
  FC,
  memo,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import { Overlay } from "../../components";
import { LANGUAGE } from "_constants";
import { Carousel } from "react-responsive-carousel";
import AdDisplay from "components/common/AdDisplay";
import AdBannerSvg from "./assets/ad-banner.svg";
import GeniusFeeds from "../../assets/ads/genius-logo.svg";
import FastGamesApi from "../../assets/ads/fastgamesapi.svg";
import Monetria from "../../assets/ads/monetria.svg";
import { BaseballStatusRenderer } from "./components/StatusRenderer";
import { Timer } from "./components/Timer";
import PreMatchIntro from "../../components/PreMatchIntro";
import { getBaseballTabIndex, BASEBALL_TABS, BaseballTabKey } from "./utils";
import { ICONS, MESSAGES } from "./constants";
import { SportTabs } from "components/common/SportTabs";
import { Stats as BaseballStats } from "./statistics";
import { PulseEffect } from "./components/PulseEffect";
import { getNormalizedStatusCode } from "utils/get-normalized-status-code";
import BaseballArena from "./assets/baseball-bg.png";
import {
  AdBanner,
  FlatLayer,
  FullHeightTabPanel,
  createBackgroundComponent,
} from "../../components/common";

export type BaseballProps = {
  status: service.MatchStatus;
  language?: LANGUAGE;
};

const StyledCourtBaseball = createBackgroundComponent({
  backgroundImage: BaseballArena,
  baseClassName:
    "w-full h-full bg-center bg-cover flex items-center justify-center relative overflow-hidden",
  displayName: "StyledCourtBaseball",
});

export const Baseball: FC<BaseballProps> = memo(
  ({ status, language = LANGUAGE.ENGLISH }) => {
    const [activeTab, setActiveTab] = useState<BaseballTabKey>("narration");
    const [displayedStatus, setDisplayedStatus] = useState(status);
    const animatingRef = useRef(false);
    const pendingRef = useRef<service.MatchStatus | null>(null);

    useEffect(() => {
      if (animatingRef.current) {
        pendingRef.current = status;
      } else {
        setDisplayedStatus(status);
      }
    }, [status]);

    const messageGenerator = useCallback(
      ({
        language,
        normalizedStatusCode,
        statusCode,
        statusMessage,
      }: {
        language: LANGUAGE;
        normalizedStatusCode?: string;
        statusCode?: string | number;
        statusMessage?: string;
      }) => {
        const langDict = MESSAGES[language];

        const collectCodes = (): string[] => {
          const codes: string[] = [];
          const push = (value: unknown) => {
            if (typeof value === "string" && value.trim().length > 0) {
              codes.push(value.trim());
            } else if (typeof value === "number" && Number.isFinite(value)) {
              codes.push(String(value));
            }
          };

          push(normalizedStatusCode);
          push(statusCode);

          return codes;
        };

        const resolveFromMap = <T,>(
          map: Record<string, T>,
          code: string
        ): T | undefined => {
          const trimmed = code.trim();
          if (!trimmed) return undefined;
          if (map[trimmed]) return map[trimmed];

          const padded = trimmed.padStart(4, "0");
          if (map[padded]) return map[padded];

          const suffixMatchKey = Object.keys(map).find((key) =>
            key.endsWith(trimmed)
          );
          if (suffixMatchKey) return map[suffixMatchKey];

          return undefined;
        };

        const codes = collectCodes();

        const lookupMessage = (): string | undefined => {
          if (!langDict) return undefined;
          for (const code of codes) {
            const resolved = resolveFromMap(langDict, code);
            if (resolved) return resolved;
          }
          return undefined;
        };

        const lookupIcon = () => {
          for (const code of codes) {
            const resolved = resolveFromMap(ICONS, code);
            if (resolved) return resolved;
          }
          return undefined;
        };

        const icon = lookupIcon();

        const humanize = (raw?: string | null) => {
          if (!raw || typeof raw !== "string") return undefined;
          const trimmed = raw.replace(/\s+/g, " ").trim();
          if (!trimmed) return undefined;
          const spaced = trimmed.replace(/([a-z])([A-Z])/g, "$1 $2");
          const fixed = spaced
            .replace(/\b([Tt]op)of\b/g, "$1 of")
            .replace(/\b([Bb]ottom)of\b/g, "$1 of")
            .replace(/\b([Ee]nd)of\b/g, "$1 of")
            .replace(/\b([Hh]alf)inning\b/g, "$1 Inning");
          return fixed;
        };

        const stringifiedCode = codes[0];

        let message =
          stringifiedCode === "1280" && statusMessage
            ? humanize(statusMessage) ?? statusMessage
            : lookupMessage();

        if ((!message || message.trim().length === 0) && statusMessage) {
          message = humanize(statusMessage) ?? statusMessage;
        }

        if (!message || message.trim().length === 0) {
          return undefined;
        }

        return { message: message.toUpperCase(), icon };
      },
      []
    );

    const normalizedCode =
      displayedStatus?.nsc ?? getNormalizedStatusCode(displayedStatus);

    const showPulseEffect =
      typeof normalizedCode === "string" &&
      (normalizedCode === "1216" || normalizedCode.endsWith("1216"));

    const isPreMatch = useMemo(() => {
      const stsVal = displayedStatus?.sts as unknown;
      const stsNum =
        typeof stsVal === "number"
          ? stsVal
          : typeof stsVal === "string"
          ? Number(stsVal)
          : NaN;
      const notStartedByStatus = !Number.isNaN(stsNum) && stsNum === 0;

      const tmVal = displayedStatus?.tm as unknown;
      const tmNum =
        typeof tmVal === "number"
          ? tmVal
          : typeof tmVal === "string"
          ? Number(tmVal)
          : NaN;
      const notStartedByTime = !Number.isNaN(tmNum) && tmNum <= 0;

      const noLiveNsc = !displayedStatus?.nsc;
      return (notStartedByStatus || notStartedByTime) && noLiveNsc;
    }, [displayedStatus]);

    return (
      <>
        <SportTabs
          tabs={BASEBALL_TABS}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as BaseballTabKey)}
        />

        <Carousel
          showThumbs={false}
          selectedItem={getBaseballTabIndex(activeTab)}
          autoPlay={false}
          emulateTouch
          showStatus={false}
          showArrows={false}
          showIndicators={false}
          swipeable={false}
        >
          <StyledCourtBaseball className="styled-court">
            <Timer status={displayedStatus} />
            <AdBanner src={AdBannerSvg} alt="Ad Banner" />
            <AdDisplay adImages={[GeniusFeeds, FastGamesApi, Monetria]} />

            <Overlay
              status={displayedStatus}
              messageGenerator={messageGenerator}
              language={language}
              StatusRenderer={BaseballStatusRenderer}
            >
              <Field />

              {showPulseEffect && <PulseEffect />}
            </Overlay>

            {isPreMatch && (
              <FlatLayer>
                <PreMatchIntro status={displayedStatus} />
              </FlatLayer>
            )}
          </StyledCourtBaseball>

          <FullHeightTabPanel>
            <BaseballStats status={status} />
          </FullHeightTabPanel>
        </Carousel>
      </>
    );
  }
);

export { StyledCourtBaseball, Overlay };
